import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import HeaderSection from "../../components/HeaderSection";
import { getAllCandidates, getCandidateById, updateCandidate, addCandidateNote } from "../../http";

const ViewCandidate = () => {
  const [candidates, setCandidates] = useState([]);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    role: "",
    status: "",
    experienceMin: "",
    experienceMax: ""
  });

  // Modal states
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  // Form states
  const [editForm, setEditForm] = useState({});
  const [noteForm, setNoteForm] = useState({ text: "", author: "" });

  const applyFilters = useCallback(() => {
    // Ensure candidates is an array before filtering
    if (!Array.isArray(candidates)) {
      setFilteredCandidates([]);
      return;
    }

    let filtered = [...candidates];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(candidate =>
        candidate.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        candidate.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        candidate.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Role filter
    if (filters.role) {
      filtered = filtered.filter(candidate =>
        candidate.appliedFor?._id === filters.role
      );
    }

    // Status filter
    if (filters.status) {
      filtered = filtered.filter(candidate =>
        candidate.status === filters.status
      );
    }

    // Experience filters
    if (filters.experienceMin) {
      filtered = filtered.filter(candidate =>
        candidate.experienceYears >= parseInt(filters.experienceMin)
      );
    }
    if (filters.experienceMax) {
      filtered = filtered.filter(candidate =>
        candidate.experienceYears <= parseInt(filters.experienceMax)
      );
    }

    setFilteredCandidates(filtered);
  }, [candidates, searchTerm, filters]);

  useEffect(() => {
    fetchCandidates();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      const response = await getAllCandidates();
      console.log("Candidates API response:", response); // Debug log

      // Handle different response formats
      let candidatesArray = [];
      
      if (Array.isArray(response)) {
        // Direct array response
        candidatesArray = response;
      } else if (response.status === 200 && Array.isArray(response.data)) {
        // Response with status and data
        candidatesArray = response.data;
      } else if (response.data && Array.isArray(response.data)) {
        // Response with data property
        candidatesArray = response.data;
      } else if (typeof response === 'object' && response.status === 200) {
        // Object with numeric indices (your case) - convert to array
        candidatesArray = Object.keys(response)
          .filter(key => !isNaN(key) && key !== 'status')
          .map(key => response[key]);
      } else {
        console.log("Unexpected response format:", response);
        toast.error("Unexpected response format from server");
        setCandidates([]);
        setFilteredCandidates([]);
        setLoading(false);
        return;
      }

      console.log("Processed candidates array:", candidatesArray);
      setCandidates(candidatesArray);
      setFilteredCandidates(candidatesArray);
    } catch (error) {
      console.error("Fetch candidates error:", error);
      toast.error("Failed to fetch candidates");
      setCandidates([]);
      setFilteredCandidates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const viewCandidate = async (candidate) => {
    try {
      const response = await getCandidateById(candidate._id);
      if (response.status === 200) {
        setSelectedCandidate(response);
        setShowViewModal(true);
      }
    } catch (error) {
      toast.error("Failed to fetch candidate details");
    }
  };

  const editCandidate = (candidate) => {
    setSelectedCandidate(candidate);
    setEditForm({
      phone: candidate.phone,
      resumeUrl: candidate.resumeUrl,
      status: candidate.status
    });
    setShowEditModal(true);
  };

  const updateCandidateData = async () => {
    try {
      const response = await updateCandidate(selectedCandidate._id, editForm);
      if (response.status === 200) {
        toast.success("Candidate updated successfully");
        setShowEditModal(false);
        fetchCandidates(); // Refresh list
      }
    } catch (error) {
      toast.error("Failed to update candidate");
    }
  };

  const addNote = async () => {
    if (!noteForm.text.trim()) {
      return toast.error("Please enter a note");
    }

    try {
      const response = await addCandidateNote(selectedCandidate._id, noteForm);
      if (response.status === 200) {
        toast.success("Note added successfully");
        setShowNoteModal(false);
        setNoteForm({ text: "", author: "" });
        fetchCandidates(); // Refresh list
      }
    } catch (error) {
      toast.error("Failed to add note");
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      APPLIED: "badge-warning",
      SHORTLISTED: "badge-info",
      INTERVIEW: "badge-primary",
      OFFER: "badge-success",
      JOINED: "badge-dark"
    };
    return <span className={`badge ${statusColors[status] || 'badge-secondary'}`}>{status}</span>;
  };

  return (
    <div className="main-content">
      <section className="section">
        <HeaderSection title="View Candidates" />
        <div className="card">
          <div className="card-body">
            {/* Search and Filters */}
            <div className="row mb-4">
              <div className="col-md-4">
                <div className="input-group">
                  <div className="input-group-prepend">
                    <div className="input-group-text">
                      <i className="fas fa-search"></i>
                    </div>
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={handleSearch}
                  />
                </div>
              </div>
              <div className="col-md-2">
                <select
                  className="form-control"
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                >
                  <option value="">All Status</option>
                  <option value="APPLIED">Applied</option>
                  <option value="SHORTLISTED">Shortlisted</option>
                  <option value="INTERVIEW">Interview</option>
                  <option value="OFFER">Offer</option>
                  <option value="JOINED">Joined</option>
                </select>
              </div>
              <div className="col-md-2">
                <input
                  type="number"
                  className="form-control"
                  name="experienceMin"
                  placeholder="Min Experience"
                  value={filters.experienceMin}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="col-md-2">
                <input
                  type="number"
                  className="form-control"
                  name="experienceMax"
                  placeholder="Max Experience"
                  value={filters.experienceMax}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="col-md-2">
                <button className="btn btn-secondary" onClick={() => {
                  setSearchTerm("");
                  setFilters({ role: "", status: "", experienceMin: "", experienceMax: "" });
                }}>
                  Clear Filters
                </button>
              </div>
            </div>

            {/* Candidates Table */}
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Experience</th>
                      <th>Applied For</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCandidates.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center py-4">
                          No candidates found
                        </td>
                      </tr>
                    ) : (
                      filteredCandidates.map((candidate) => (
                        <tr key={candidate._id}>
                          <td>
                            {candidate.firstName} {candidate.lastName}
                          </td>
                          <td>{candidate.email}</td>
                          <td>{candidate.phone}</td>
                          <td>{candidate.experienceYears} years</td>
                          <td>{candidate.appliedFor?.title || 'N/A'}</td>
                          <td>{getStatusBadge(candidate.status)}</td>
                          <td>
                            <div className="d-flex">
                              <span className="mr-2">
                                <button
                                  className="btn btn-sm btn-info"
                                  onClick={() => viewCandidate(candidate)}
                                  title="View Details"
                                >
                                  <i className="fas fa-eye"></i>
                                </button>
                              </span>
                              <span className="mr-2">
                                <button
                                  className="btn btn-sm btn-warning"
                                  onClick={() => editCandidate(candidate)}
                                  title="Edit"
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                              </span>
                              <span>
                                <button
                                  className="btn btn-sm btn-success"
                                  onClick={() => {
                                    setSelectedCandidate(candidate);
                                    setShowNoteModal(true);
                                  }}
                                  title="Add Note"
                                >
                                  <i className="fas fa-sticky-note"></i>
                                </button>
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* View Modal */}
      {showViewModal && selectedCandidate && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Candidate Details</h5>
                <button type="button" className="close" onClick={() => setShowViewModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <p><strong>Name:</strong> {selectedCandidate.firstName} {selectedCandidate.lastName}</p>
                    <p><strong>Email:</strong> {selectedCandidate.email}</p>
                    <p><strong>Phone:</strong> {selectedCandidate.phone}</p>
                    <p><strong>Experience:</strong> {selectedCandidate.experienceYears} years</p>
                  </div>
                  <div className="col-md-6">
                    <p><strong>Status:</strong> {getStatusBadge(selectedCandidate.status)}</p>
                    <p><strong>Applied For:</strong> {selectedCandidate.appliedFor?.title || 'N/A'}</p>
                    <p><strong>Resume:</strong> <a href={selectedCandidate.resumeUrl} target="_blank" rel="noopener noreferrer">View Resume</a></p>
                    <p><strong>Source:</strong> {selectedCandidate.source}</p>
                  </div>
                </div>
                {selectedCandidate.notes && selectedCandidate.notes.length > 0 && (
                  <div className="mt-4">
                    <h6>Notes:</h6>
                    {selectedCandidate.notes.map((note, index) => (
                      <div key={index} className="alert alert-light">
                        <small>{note.text}</small>
                        <br />
                        <small className="text-muted">- {note.author}</small>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowViewModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedCandidate && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Candidate</h5>
                <button type="button" className="close" onClick={() => setShowEditModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Resume URL</label>
                  <input
                    type="url"
                    className="form-control"
                    value={editForm.resumeUrl}
                    onChange={(e) => setEditForm({ ...editForm, resumeUrl: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select
                    className="form-control"
                    value={editForm.status}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  >
                    <option value="APPLIED">Applied</option>
                    <option value="SHORTLISTED">Shortlisted</option>
                    <option value="INTERVIEW">Interview</option>
                    <option value="OFFER">Offer</option>
                    <option value="JOINED">Joined</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={updateCandidateData}>
                  Update
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Note Modal */}
      {showNoteModal && selectedCandidate && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Note - {selectedCandidate.firstName} {selectedCandidate.lastName}</h5>
                <button type="button" className="close" onClick={() => setShowNoteModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Note</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    value={noteForm.text}
                    onChange={(e) => setNoteForm({ ...noteForm, text: e.target.value })}
                    placeholder="Enter your note here..."
                  ></textarea>
                </div>
                <div className="form-group">
                  <label>Author</label>
                  <input
                    type="text"
                    className="form-control"
                    value={noteForm.author}
                    onChange={(e) => setNoteForm({ ...noteForm, author: e.target.value })}
                    placeholder="Your name"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowNoteModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={addNote}>
                  Add Note
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewCandidate;